import React from 'react';
import {
  Box, Typography, TextField, IconButton, Tooltip, TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Button, List, ListItem, ListItemText, Divider, CircularProgress, Alert, Tabs, Tab, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import TokenIcon from '../components/TokenIcon';
import AddTransactionModal from '../AddTransactionModal';
import { useWalletsPage } from '../hooks/useWalletsPage';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Menu from '@mui/material/Menu';
import ListItemIcon from '@mui/material/ListItemIcon';
import { useLocation } from 'react-router-dom';
import { useLanguageNavigation } from '../hooks/useLanguageNavigation';

function ManualHoldingsPage() {
  const { t } = useTranslation();
  const {
    manualHoldingsSearchTerm,
    setManualHoldingsSearchTerm,
    manualHoldingsLoading,
    manualHoldingsAllColumns,
    manualHoldingsVisibleColumns,
    manualHoldingsSortColumn,
    manualHoldingsSortDirection,
    manualHoldingsColumnRenderers,
    sortedManualHoldings,
    handleManualHoldingsSort,
    handleManualHoldingsOpenColumnsDialog,
    manualHoldingsColumnsDialogOpen,
    manualHoldingsTempVisibleColumns,
    manualHoldingsTempHiddenColumns,
    handleManualHoldingsMoveColumnUp,
    handleManualHoldingsMoveColumnDown,
    handleManualHoldingsHideColumn,
    handleManualHoldingsShowColumn,
    handleManualHoldingsCloseColumnsDialog,
    handleManualHoldingsSaveColumnsDialog,
    manualHoldingsDeleteConfirmOpen,
    handleManualHoldingsDeleteCancel,
    handleManualHoldingsDeleteConfirm,
    handleManualHoldingsDeleteClick,
    setEditingTransaction,
    setNewTransaction,
    setModalType,
    setOpen,
    editingTransaction,
    newTransaction,
    modalType,
    open,
    modalError,
    blockchains,
    blockchainsLoading,
    newWallet,
    setNewWallet,
    handleClose,
    handleSubmit,
    coinsLoading,
    handleAddManualHolding
  } = useWalletsPage();

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [menuTx, setMenuTx] = React.useState(null);
  const [isDetailedView, setIsDetailedView] = React.useState(false);
  const { navigateWithLang } = useLanguageNavigation();
  const location = useLocation();

  return (
    <Box sx={{ p: { xs: 1, sm: 3 } }}>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 2 }}>
        {t('pages.manualHoldings.title')}
      </Typography>
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder={t('manualHoldings.searchTransactions')}
          value={manualHoldingsSearchTerm}
          onChange={(e) => setManualHoldingsSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
        />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', mb: 1, gap: 1 }}>
        <Chip
          label={t('manualHoldings.addHoldings')}
          color="primary"
          size="small"
          variant="outlined"
          onClick={() => navigateWithLang('add-holdings')}
          sx={{ fontWeight: 500, fontSize: '0.9rem', height: 32, borderRadius: '999px', display: 'flex', alignItems: 'center' }}
        />
        <Chip 
          label={t('manualHoldings.detailed')} 
          color="primary" 
          size="small" 
          variant={isDetailedView ? 'filled' : 'outlined'} 
          onClick={() => setIsDetailedView(!isDetailedView)} 
          sx={{ fontWeight: 500, fontSize: '0.9rem', height: 32, borderRadius: '999px', display: 'flex', alignItems: 'center' }} 
        />
        <Tooltip title={t('common.columns')}>
          <IconButton onClick={handleManualHoldingsOpenColumnsDialog} size="large">
            <ViewColumnIcon />
          </IconButton>
        </Tooltip>
      </Box>
      {manualHoldingsLoading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : (
        !isDetailedView ? (
          // Compact mode: Token, Amount, Market Value, Date
          <Box display="flex" flexDirection="column" gap={0} sx={{ bgcolor: '#fff', borderRadius: 1 }}>
            {/* Compact view headers */}
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                py: { xs: 0.5, sm: 1 }, 
                px: { xs: 0.5, sm: 2 }, 
                borderBottom: '1px solid #e0e0e0',
                bgcolor: '#fafafa',
                fontWeight: 600,
                fontSize: '0.85rem'
              }}
            >
              <Box flex={1} minWidth={40} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {/* Empty for actions */}
              </Box>
              <Box display="flex" alignItems="center" minWidth={60} flex={2} sx={{ mr: { xs: 0.5, sm: 1 } }}>
                {t('tables.transactions.coin')}
              </Box>
              <Box flex={2} minWidth={60} textAlign="center" sx={{ pr: { xs: 0.5, sm: 1 } }}>
                {t('tables.transactions.amount')}
              </Box>
              <Box flex={2} minWidth={60} textAlign="center" sx={{ pr: { xs: 0.5, sm: 1 } }}>
                {t('tables.portfolio.marketValue')}
              </Box>
              <Box flex={2} minWidth={60} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                {t('tables.transactions.date')}
              </Box>
            </Box>
            {(sortedManualHoldings || []).map((tx, index) => {
              const marketValue = manualHoldingsColumnRenderers.total(tx)?.current ?? '-';
              // Format date with date and time, like TransactionsList
              let dateString = '-';
              if (tx.date) {
                try {
                  const d = new Date(tx.date);
                  if (!isNaN(d.getTime())) {
                    dateString = d.toLocaleDateString() + ' ' + d.toLocaleTimeString();
                  }
                } catch {}
              }
              return (
                <Box 
                  key={tx.id ?? index} 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    py: { xs: 0.5, sm: 1.5 }, 
                    px: { xs: 0.5, sm: 2 }, 
                    borderBottom: index < sortedManualHoldings.length - 1 ? '1px solid #f0f0f0' : 'none',
                    width: '100%', 
                    '&:last-child': { borderBottom: 'none' }
                  }}
                >
                  {/* Actions */}
                  <Box flex={1} minWidth={40} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <IconButton
                      aria-label="more"
                      aria-controls={Boolean(anchorEl) ? 'manual-holdings-menu' : undefined}
                      aria-haspopup="true"
                      onClick={event => {
                        setAnchorEl(event.currentTarget);
                        setMenuTx(tx);
                      }}
                      size="small"
                      sx={{ m: 0, p: 0.5 }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  {/* Token icon and symbol only */}
                  <Box display="flex" alignItems="center" minWidth={60} flex={2} sx={{ mr: { xs: 0.5, sm: 1 }, gap: 1 }}>
                    <TokenIcon coinSymbol={tx.coin} size={22} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: { xs: '0.95rem', sm: '1.1rem' }, lineHeight: 1.2 }}>{tx.coin}</Typography>
                  </Box>
                  {/* Amount */}
                  <Box flex={2} minWidth={60} textAlign="center" sx={{ pr: { xs: 0.5, sm: 1 } }}>
                    <Typography variant="subtitle2" color={tx.type === 'Buy' ? 'success.main' : 'error.main'} sx={{ fontWeight: 600, fontSize: { xs: '0.92rem', sm: '1rem' }, lineHeight: 1.2 }}>
                      {tx.type === 'Buy' ? '+' : '-'}{Math.abs(Number(tx.amount)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })}
                    </Typography>
                  </Box>
                  {/* Market Value */}
                  <Box flex={2} minWidth={60} textAlign="center" sx={{ pr: { xs: 0.5, sm: 1 } }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: '0.92rem', sm: '1rem' }, lineHeight: 1.2 }}>
                      {marketValue}
                    </Typography>
                  </Box>
                  {/* Date */}
                  <Box flex={2} minWidth={60} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    <Typography variant="caption" color="textSecondary" sx={{ lineHeight: 1.2, fontSize: { xs: '0.7rem', sm: '0.85rem' } }}>
                      {dateString}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        ) : (
          // Detailed mode: current table
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell></TableCell>
                  {(manualHoldingsAllColumns || [])
                    .filter(col => (manualHoldingsVisibleColumns || []).includes(col.id))
                    .map(col => (
                      <TableCell
                        key={col.id}
                        align={col.align}
                        onClick={() => handleManualHoldingsSort(col.id)}
                        sx={{
                          cursor: 'pointer',
                          '& .MuiBox-root': {
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            flexDirection: 'row'
                          }
                        }}
                      >
                        <Box>
                          {(() => {
                            // If col.label is already a translated string, use as is
                            // If it's a known key, use t(key)
                            if (col.id === 'blockchain') return t('tables.portfolio.blockchain');
                            if (col.id === 'price') return t('tables.transactions.price');
                            if (col.id === 'total') return t('tables.transactions.total');
                            return col.label;
                          })()}
                          {manualHoldingsSortColumn === col.id && (
                            <Box component="span" sx={{ display: 'inline-flex', verticalAlign: 'middle' }}>
                              {manualHoldingsSortDirection === 'asc' ? <ArrowDropUpIcon fontSize="small" /> : <ArrowDropDownIcon fontSize="small" />}
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                    ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {(sortedManualHoldings || []).map((tx, idx) => (
                  <TableRow key={tx.id ?? idx}>
                    <TableCell align="center" sx={{ p: 0, width: 0, minWidth: 0, maxWidth: 36, textAlign: 'center' }}>
                      <IconButton
                        aria-label="more"
                        aria-controls={Boolean(anchorEl) ? 'manual-holdings-menu' : undefined}
                        aria-haspopup="true"
                        onClick={event => {
                          setAnchorEl(event.currentTarget);
                          setMenuTx(tx);
                        }}
                        size="small"
                        sx={{ m: 0, p: 0 }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                    {(manualHoldingsVisibleColumns || []).includes('date') && (
                      <TableCell>{manualHoldingsColumnRenderers.date(tx)}</TableCell>
                    )}
                    {(manualHoldingsVisibleColumns || []).includes('type') && (
                      <TableCell>
                        <Chip
                          label={manualHoldingsColumnRenderers.type(tx)}
                          color={tx.type === 'Buy' ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                    )}
                    {(manualHoldingsVisibleColumns || []).includes('coin') && (
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TokenIcon coinSymbol={tx.coin} size={24} />
                          {manualHoldingsColumnRenderers.coin(tx)}
                        </Box>
                      </TableCell>
                    )}
                    {(manualHoldingsVisibleColumns || []).includes('coinName') && (
                      <TableCell>{manualHoldingsColumnRenderers.coinName(tx)}</TableCell>
                    )}
                    {(manualHoldingsVisibleColumns || []).includes('blockchain') && (
                      <TableCell>
                        <Chip
                          label={manualHoldingsColumnRenderers.blockchain(tx)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                    )}
                    {(manualHoldingsVisibleColumns || []).includes('amount') && (
                      <TableCell align="right">{manualHoldingsColumnRenderers.amount(tx)}</TableCell>
                    )}
                    {(manualHoldingsVisibleColumns || []).includes('price') && (
                      <TableCell align="right">
                        <Box>
                          <Typography variant="body2" color="textSecondary">
                            {manualHoldingsColumnRenderers.price(tx).historical}
                          </Typography>
                          <Typography variant="body2" color="primary">
                            {manualHoldingsColumnRenderers.price(tx).current}
                          </Typography>
                        </Box>
                      </TableCell>
                    )}
                    {(manualHoldingsVisibleColumns || []).includes('fee') && (
                      <TableCell align="right">{manualHoldingsColumnRenderers.fee(tx)}</TableCell>
                    )}
                    {(manualHoldingsVisibleColumns || []).includes('total') && (
                      <TableCell align="right">
                        <Box>
                          <Typography variant="body2" color="textSecondary">
                            {manualHoldingsColumnRenderers.total(tx).historical}
                          </Typography>
                          <Typography variant="body2" color="primary">
                            {manualHoldingsColumnRenderers.total(tx).current}
                          </Typography>
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      )}
      
      {/* Manual Holdings Menu - Shared between compact and detailed modes */}
      <Menu
        id="manual-holdings-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem onClick={() => {
          setNewTransaction({
            date: menuTx?.date ? new Date(menuTx.date).toISOString().slice(0, 10) : '',
            type: menuTx?.type,
            coin: menuTx?.coin,
            blockchain: menuTx?.blockchain || '',
            amount: parseFloat(menuTx?.amount || 0),
            shares: (parseFloat(menuTx?.amount || 0)).toString(),
            buy_price: parseFloat((menuTx?.buy_price ?? menuTx?.price ?? menuTx?.costPerShare) || 0),
            costPerShare: (parseFloat((menuTx?.buy_price ?? menuTx?.price ?? menuTx?.costPerShare) || 0)).toString(),
            price: parseFloat((menuTx?.buy_price ?? menuTx?.price ?? menuTx?.costPerShare) || 0),
            fee: (parseFloat(menuTx?.fee || 0)).toString()
          });
          setEditingTransaction(menuTx);
          setModalType('transaction');
          setOpen(true);
          setAnchorEl(null);
        }}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('common.edit')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          handleManualHoldingsDeleteClick(menuTx?.id);
          setAnchorEl(null);
        }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('common.delete')}</ListItemText>
        </MenuItem>
      </Menu>
      
      {/* Manual Holdings Columns Dialog */}
      <Dialog open={manualHoldingsColumnsDialogOpen} onClose={handleManualHoldingsCloseColumnsDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{t('common.columns')}</DialogTitle>
        <DialogContent>
          <List>
            {(manualHoldingsTempVisibleColumns || []).map((colId, index) => {
              const col = (manualHoldingsAllColumns || []).find(c => c.id === colId);
              return (
                <ListItem
                  key={colId}
                  style={{ display: 'flex', alignItems: 'center' }}
                  secondaryAction={
                    <>
                      <IconButton edge="end" onClick={() => handleManualHoldingsMoveColumnUp(index)} disabled={index === 0}>
                        <ArrowUpwardIcon />
                      </IconButton>
                      <IconButton edge="end" onClick={() => handleManualHoldingsMoveColumnDown(index)} disabled={index === (manualHoldingsTempVisibleColumns || []).length - 1}>
                        <ArrowDownwardIcon />
                      </IconButton>
                      <IconButton edge="end" onClick={() => handleManualHoldingsHideColumn(colId)} disabled={(manualHoldingsTempVisibleColumns || []).length === 1}>
                        <VisibilityOffIcon />
                      </IconButton>
                    </>
                  }
                >
                  <ListItemText primary={col?.label || colId} />
                </ListItem>
              );
            })}
          </List>
          <Divider sx={{ my: 1 }} />
          <List>
            {(manualHoldingsTempHiddenColumns || []).map((colId) => {
              const col = (manualHoldingsAllColumns || []).find(c => c.id === colId);
              return (
                <ListItem key={colId} secondaryAction={
                  <IconButton edge="end" onClick={() => handleManualHoldingsShowColumn(colId)}>
                    <VisibilityIcon />
                  </IconButton>
                }>
                  <ListItemText primary={col?.label || colId} />
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleManualHoldingsCloseColumnsDialog}>{t('common.cancel')}</Button>
          <Button onClick={handleManualHoldingsSaveColumnsDialog} variant="contained" autoFocus>{t('common.saveChanges')}</Button>
        </DialogActions>
      </Dialog>
      {/* Manual Holdings Delete Confirmation Dialog */}
      <Dialog
        open={manualHoldingsDeleteConfirmOpen}
        onClose={handleManualHoldingsDeleteCancel}
        aria-labelledby="manual-holdings-delete-dialog-title"
        aria-describedby="manual-holdings-delete-dialog-description"
      >
        <DialogTitle id="manual-holdings-delete-dialog-title">
          {t('dialogs.deleteTransaction.title')}
        </DialogTitle>
        <DialogContent>
          <Typography id="manual-holdings-delete-dialog-description">
            {t('dialogs.deleteTransaction.message')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleManualHoldingsDeleteCancel} color="primary">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleManualHoldingsDeleteConfirm} color="error" variant="contained" autoFocus>
            {t('dialogs.deleteTransaction.confirmButton')}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Add/Edit Manual Holdings Modal */}
      <Dialog open={open} onClose={() => handleClose(true)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingTransaction ? t('portfolio.editHoldings') : t('portfolio.addHoldings')}</DialogTitle>
        <Tabs
          value={modalType}
          onChange={(_, newValue) => setModalType(newValue)}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label={t('manualHoldings.addWallet')} value="wallet" />
          <Tab label={editingTransaction ? t('manualHoldings.editManualTransaction') : t('manualHoldings.addManualTransaction')} value="transaction" />
        </Tabs>
        <Box sx={{ p: { xs: 1, sm: 3 } }}>
          {modalType === 'wallet' ? (
            <>
              {modalError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {modalError}
                </Alert>
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                <FormControl fullWidth required error={Boolean(modalError && !newWallet.blockchain)}>
                  <InputLabel>{t('common.blockchain')}</InputLabel>
                  <Select
                    value={newWallet.blockchain}
                    label={t('common.blockchain')}
                    onChange={(e) => setNewWallet(prev => ({ ...prev, blockchain: e.target.value }))}
                    disabled={blockchainsLoading}
                  >
                    {blockchains.map((chain, index) => (
                      <MenuItem 
                        key={chain.symbol || `blockchain-${index}`} 
                        value={(chain.symbol || '').toLowerCase()}
                        disabled={!chain.is_supported}
                        sx={{
                          opacity: chain.is_supported ? 1 : 0.5,
                          color: chain.is_supported ? 'inherit' : 'text.disabled'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <img 
                            src={chain.icon_url} 
                            alt={chain.name} 
                            style={{ 
                              width: 24, 
                              height: 24,
                              opacity: chain.is_supported ? 1 : 0.5
                            }} 
                          />
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <span>{chain.name}</span>
                            {!chain.is_supported && (
                              <span style={{ 
                                fontSize: '0.75rem', 
                                color: 'text.disabled',
                                fontStyle: 'italic'
                              }}>
                                Coming Soon
                              </span>
                            )}
                          </Box>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label={t('common.address')}
                  value={newWallet.address}
                  onChange={(e) => setNewWallet(prev => ({ ...prev, address: e.target.value }))}
                  fullWidth
                  required
                  error={Boolean(modalError && !newWallet.address)}
                  helperText={modalError && !newWallet.address ? t('common.enterAddress') : ''}
                />
                <TextField
                  label={t('common.walletLabel')}
                  value={newWallet.label}
                  onChange={(e) => setNewWallet(prev => ({ ...prev, label: e.target.value }))}
                  fullWidth
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
                <Button onClick={() => handleClose(true)} color="secondary" variant="outlined" sx={{ minWidth: 100 }}>
                  {t('common.cancel')}
                </Button>
                <Button onClick={handleSubmit} variant="contained" sx={{ minWidth: 100 }}>
                  {t('common.addWallet')}
                </Button>
              </Box>
            </>
          ) : (
            <AddTransactionModal
              formOnly={true}
              onClose={() => handleClose(true)}
              onSubmit={handleAddManualHolding}
              loading={coinsLoading}
              initialValues={newTransaction}
              error={modalError}
              submitButtonText={editingTransaction ? t('manualHoldings.updateTransaction') : t('manualHoldings.addTransaction')}
              editing={!!editingTransaction}
            />
          )}
        </Box>
      </Dialog>
    </Box>
  );
}

export default ManualHoldingsPage; 